// 数字 / 时间 / 文案格式化。规范见设计系统 README：千位窄空格「$4 750」，比分「13 : 11」，时间「1:47」。
import type { DemoStatus, MapInfo, TeamRole } from "./api/types";

const NNBSP = " ";

export function groupThousands(n: number): string {
  const s = Math.round(n).toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
}

export const money = (n: number) => `$${groupThousands(n)}`;

export function fixed(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toFixed(digits);
}

export function pct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

/** 秒 → m:ss */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** 秒 → h:mm:ss / m:ss */
export function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  if (h > 0) return `${h}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  return clock(s);
}

export function fileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** 2026-10-11 */
export function date(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 2026-10-11 21:30 */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${date(iso)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 列表里的短时间：刚刚 / 3 分钟前 / 今天 21:30 / 昨天 22:40 / 10-11 21:30 / 2025-10-11 */
export function relTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return "刚刚";
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  const today = new Date(now);
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return `${Math.floor(diff / 3600)} 小时前`;
  const y = new Date(now - 86400000);
  if (sameDay(d, y)) return `昨天 ${hm}`;
  if (d.getFullYear() === today.getFullYear()) return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${hm}`;
  return date(iso);
}

export const roleLabel: Record<TeamRole, string> = { owner: "队长", coach: "教练", analyst: "分析师", player: "队员" };
/** 角色徽标色调。 */
export const roleTone = (r: TeamRole): Tone => (r === "owner" ? "primary" : r === "coach" ? "warning" : r === "analyst" ? "neutral" : "ct");
/** 能否上传、修改或删除 demo（分析师不能；战术库不按这个判断）。 */
export const canMutate = (r: TeamRole) => r !== "analyst";

export const demoStatusLabel: Record<DemoStatus, string> = {
  pending_upload: "等待上传",
  uploaded: "已上传",
  queued: "排队中",
  parsing: "解析中",
  ready: "已就绪",
  failed: "解析失败",
};

export type Tone = "neutral" | "ct" | "t" | "primary" | "success" | "warning" | "danger";

export const demoStatusTone: Record<DemoStatus, Tone> = {
  pending_upload: "neutral",
  uploaded: "neutral",
  queued: "neutral",
  parsing: "warning",
  ready: "success",
  failed: "danger",
};

export const sourceLabel: Record<string, string> = {
  valve: "Valve MM",
  faceit: "FACEIT",
  "5eplay": "5E",
  perfectworld: "完美世界",
  esea: "ESEA",
  esl: "ESL",
  matchzy: "MatchZy",
  other_platform: "其它平台",
  unknown: "—",
};

export const sourceText = (s: string | null | undefined) => (s ? sourceLabel[s] ?? s : "—");

/** de_mirage → { zh: 荒漠迷城, en: Mirage } */
export function mapNames(name: string | null | undefined, maps?: MapInfo[]): { zh: string; en: string } {
  if (!name) return { zh: "—", en: "" };
  const m = maps?.find((x) => x.name === name);
  if (m) return { zh: m.display_name_zh || m.display_name, en: m.display_name };
  const en = name.replace(/^(de|cs|ar)_/, "");
  return { zh: en.charAt(0).toUpperCase() + en.slice(1), en: "" };
}

/** 首字母 / 首字（头像占位）。 */
export function initial(name: string | null | undefined): string {
  const s = (name ?? "").trim();
  if (!s) return "?";
  const ch = Array.from(s)[0];
  return ch.toUpperCase();
}

export function demoTitle(d: { title: string | null; original_filename: string }): string {
  return d.title?.trim() || d.original_filename;
}

/** Round end reason（demoinfocs RoundEndReason）→ 中文 + 类别。 */
export type EndKind = "elim" | "explode" | "defuse" | "time" | "other";
export function endReason(reason: number): { label: string; kind: EndKind } {
  switch (reason) {
    case 1:
      return { label: "炸弹爆炸", kind: "explode" };
    case 7:
      return { label: "拆除成功", kind: "defuse" };
    case 8:
    case 9:
      return { label: "歼灭", kind: "elim" };
    case 12:
      return { label: "时间耗尽", kind: "time" };
    case 10:
      return { label: "平局", kind: "other" };
    case 17:
    case 18:
      return { label: "投降", kind: "other" };
    default:
      return { label: "回合结束", kind: "other" };
  }
}

/** v1 无付费功能：plan 字段预留，free 显示为免费版。 */
export function planLabel(plan: string | null | undefined): string {
  if (!plan || plan === "free") return "免费版";
  if (plan === "team") return "战队版";
  if (plan === "pro") return "专业版";
  return plan;
}

/** steamid64 → 7656…2207 */
export const shortSteam = (id: string) => (id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id);

/** 把 User-Agent 简化成「Chrome · macOS」。 */
export function deviceLabel(ua: string | null | undefined): string {
  if (!ua) return "未知设备";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : /curl\//.test(ua)
              ? "curl"
              : /Go-http-client/.test(ua)
                ? "Go 客户端"
                : "浏览器";
  const os = /iPhone/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua)
      ? "iPad"
      : /Android/.test(ua)
        ? "Android"
        : /Windows/.test(ua)
          ? "Windows"
          : /Mac OS X|Macintosh/.test(ua)
            ? "macOS"
            : /Linux/.test(ua)
              ? "Linux"
              : "";
  return os ? `${browser} · ${os}` : browser;
}
